import { useTranslation } from 'react-i18next';
import { NettleMark } from '@shared/components/Brand/NettleMark';
import './SplashScreen.css';

interface SplashScreenProps {
  /** Подпись под прогресс-баром. По умолчанию — i18n `splash.status`. */
  message?: string;
  /**
   * Реальный прогресс инициализации 0–100. Если передан — бар детерминированный
   * (ширина = прогресс). Если не передан — честный indeterminate-бар (длительность неизвестна).
   */
  progress?: number;
}

/**
 * Брендовый сплэш-экран WorkerNet «Крапива · V5».
 *
 * Порт дизайна `Лого листка крапивы/Сплэш Крапива.dc.html`.
 * Тёмный брендовый фон вне зависимости от темы приложения — это момент запуска.
 * Анимации compositor-friendly (transform/opacity), отключаются при prefers-reduced-motion.
 * Шрифт — системный стек (CSP запрещает Google Fonts в проде), начертание 800.
 */
export function SplashScreen({ message, progress }: SplashScreenProps) {
  const { t } = useTranslation();
  // Во время раннего бута бандл перевода может быть ещё не загружен —
  // defaultValue гарантирует осмысленный русский текст (lang=ru по умолчанию).
  const status = message ?? t('splash.status', 'Подключение к узлам сети');

  const isDeterminate = typeof progress === 'number';
  const clampedProgress = isDeterminate ? Math.max(0, Math.min(100, progress as number)) : undefined;

  return (
    <div className="kp-splash">
      <div className="kp-splash__inner">
        <div className="kp-splash__mark">
          <div className="kp-splash__ring" />
          <div className="kp-splash__mark-anim">
            <NettleMark size={132} gradientId="kpSplashTint" />
          </div>
        </div>

        <div className="kp-splash__word-wrap">
          <div className="kp-splash__word">WorkerNet</div>
          <div className="kp-splash__tag">К Р А П И В А · V5</div>
        </div>

        <div
          className="kp-splash__bar"
          role="progressbar"
          aria-label={status}
          aria-valuemin={isDeterminate ? 0 : undefined}
          aria-valuemax={isDeterminate ? 100 : undefined}
          aria-valuenow={clampedProgress}
        >
          {isDeterminate ? (
            <div
              className="kp-splash__bar-fill kp-splash__bar-fill--determinate"
              style={{ width: `${clampedProgress}%` }}
            />
          ) : (
            <div className="kp-splash__bar-fill kp-splash__bar-fill--indeterminate" />
          )}
        </div>

        <div className="kp-splash__status">
          <span>{status}</span>
          <span className="kp-splash__dots">
            <span className="kp-splash__dot" />
            <span className="kp-splash__dot" />
            <span className="kp-splash__dot" />
          </span>
        </div>
      </div>
    </div>
  );
}
